import {
	Accordion,
	AccordionDetails,
	AccordionSummary,
	Alert,
	Box,
	CircularProgress,
	Paper,
	Tooltip,
	Typography, useTheme,
} from "@mui/material";
import { intl } from "LocaleWrapper";
import { FormattedDate } from "utils/DateUtils";
import ExpandMoreRoundedIcon from "@mui/icons-material/ExpandMoreRounded";
import OverflowTooltip from "components/cck/OverflowTooltip";
import {
	GeneralEntityRevisionFragment,
} from "__generated__/components/entityHistory/graphql/entityHistory.fragments.generated";
import { ReactNode } from "react";
import { ApolloError } from "@apollo/client";

type EntityHistoryRevisionsProps<T extends GeneralEntityRevisionFragment,> = {
	revisions: T[];
	loading: boolean;
	error: ApolloError | null | undefined;
	renderEmptyRemarkTitle: (revision: T) => ReactNode;
	renderRevision: (revision: T) => ReactNode;
};

const EntityHistoryRevisions = <T extends GeneralEntityRevisionFragment,>({
	revisions,
	loading,
	error,
	renderEmptyRemarkTitle,
	renderRevision,
}: EntityHistoryRevisionsProps<T>) => {
	const theme = useTheme();

	if (loading) {
		return (
			<Box display="flex" justifyContent="center" py={4}>
				<CircularProgress size={28} />
			</Box>
		);
	}

	if (error) {
		return (
			<Box display="flex" justifyContent="center" p={2}>
				<Alert severity="error" sx={{ flexGrow: 1 }}>
					<Typography variant="body2" color="error">
						{intl.formatMessage({ id: "HISTORY_LOAD_ERROR" })}
					</Typography>
				</Alert>
			</Box>
		);
	}

	if (revisions.length === 0) {
		return (
			<Box display="flex" justifyContent="center" p={2}>
				<Alert severity="warning" sx={{ flexGrow: 1 }}>
					<Typography variant="body2" color="text.secondary">
						{intl.formatMessage({ id: "HISTORY_NO_REVISIONS" })}
					</Typography>
				</Alert>
			</Box>
		);
	}

	const sorted = [ ...revisions ].sort((a, b) => b.changedAt - a.changedAt);

	return (
		<Box display="flex" flexDirection="column">
			{sorted.map(revision => (
				<Accordion
					key={revision.id}
					defaultExpanded
					square
					disableGutters
					sx={{
						marginBottom: 2,
						"&::before": {
							display: "none",
						},
					}}
				>
					<AccordionSummary
						expandIcon={<ExpandMoreRoundedIcon fontSize="small" color="primary" />}
						slotProps={{
							content: {
								sx: {
									minWidth: 0,
								},
							},
						}}
					>
						<Box
							display="flex"
							justifyContent="space-between"
							paddingRight={1}
							flexWrap="nowrap"
							width="100%"
						>
							<Typography
								textTransform="none"
								fontWeight="bold"
								fontSize={14}
								maxWidth="75%"
							>
								{revision.remark?.includes("\n") ? (
									<Tooltip
										title={<span style={{ whiteSpace: "pre-wrap" }}>{revision.remark}</span>}>
										<span style={{
											display: "inline-block",
											maxWidth: "100%",
											overflow: "hidden",
											textOverflow: "ellipsis",
											whiteSpace: "nowrap",
										}}>
											{revision.remark}
										</span>
									</Tooltip>
								) : revision.remark ? (
									<OverflowTooltip>
										{revision.remark}
									</OverflowTooltip>
								) : renderEmptyRemarkTitle(revision)}
							</Typography>

							<span>
								<Typography
									component="span"
									color="text.secondary"
									variant="subtitle2"
								>
									{revision.changedBy.displayName ?? intl.formatMessage({ id: "UNKNOWN" })}
								</Typography>
								{" - "}
								<Typography
									component="span"
									textTransform="none"
									color="text.secondary"
									variant="subtitle2"
								>
									<FormattedDate date={revision.changedAt} />
								</Typography>
							</span>
						</Box>
					</AccordionSummary>
					<AccordionDetails
						sx={{
							padding: 0,
							borderTopWidth: "1px",
							borderTopStyle: "solid",
							borderTopColor: "divider",
						}}
					>
						<Paper
							square
							elevation={0}
							sx={{
								padding: 2,
								backgroundColor: theme.palette.mode === "dark" ? "inherit" : "grey.100",
							}}
						>
							{renderRevision(revision)}
						</Paper>
					</AccordionDetails>
				</Accordion>
			))}
		</Box>
	);
};

export default EntityHistoryRevisions;
